import * as T from 'three';
import { GATE, type FlightPoint } from './profile';

// The VIP chopper (as Lagos Life's "VIP CHOPPER · Straight to the door"): a twin-engine helicopter in the
// airline's colours, the main rotor and the tail rotor turning, skids, a glass nose. Built at life size in metres
// (about 13 m long), nose along +x. Its path lifts straight up off the helipad, leans forward and climbs away
// low over London, then slows, hovers and comes straight down at the door.

export const CHOPPER_HEIGHT = 120;
// from its middle down to the bottom of the skids
export const CHOPPER_GROUND = 1.55;

export type Chopper = {
  group: T.Group;
  update: (seconds: number) => void;
  dispose: () => void;
};

export function buildChopper(colours: { body: string; tail: string; stripe: string }): Chopper {
  const group = new T.Group();
  const disposables: { dispose: () => void }[] = [];
  const keep = <D extends { dispose: () => void }>(item: D) => (disposables.push(item), item);
  const paint = (color: string, extra: Partial<T.MeshStandardMaterialParameters> = {}) =>
    keep(new T.MeshStandardMaterial({ color, roughness: 0.4, metalness: 0.15, ...extra }));
  const body = paint(colours.tail);
  const stripe = paint(colours.stripe);
  const white = paint(colours.body);
  const glass = paint('#1b2633', { roughness: 0.1, metalness: 0.5 });
  const metal = paint('#9aa3ab', { metalness: 0.6 });
  const dark = paint('#2a2f36');
  const add = (geometry: T.BufferGeometry, material: T.Material, x = 0, y = 0, z = 0, parent: T.Object3D = group) => {
    keep(geometry);
    const mesh = new T.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    parent.add(mesh);
    return mesh;
  };
  // the cabin: a stretched egg, the glass nose, a stripe down the side
  const cabin = add(new T.SphereGeometry(1.6, 28, 18), body, 0.6, 0.2, 0);
  cabin.scale.set(2.1, 1.05, 0.95);
  const nose = add(new T.SphereGeometry(1.3, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.55), glass, 2.6, 0.45, 0);
  nose.rotation.z = -Math.PI / 2 + 0.35;
  nose.scale.set(1, 1.15, 1.05);
  for (const side of [-1, 1]) {
    const band = add(new T.BoxGeometry(5.2, 0.28, 0.05), stripe, 0.4, -0.25, side * 1.5);
    band.rotation.y = 0;
    add(new T.BoxGeometry(1.9, 0.75, 0.05), glass, 0.2, 0.55, side * 1.49);
  }
  // the engines on the roof and the tail boom, tapering, with its fin and the tail rotor
  add(new T.BoxGeometry(2.6, 0.7, 1.3), white, 0, 1.75, 0);
  const boom = add(new T.CylinderGeometry(0.22, 0.55, 6.5, 14), body, -4.6, 0.75, 0);
  boom.rotation.z = Math.PI / 2 - 0.06;
  const fin = add(new T.BoxGeometry(1.1, 1.8, 0.12), body, -7.7, 1.6, 0);
  fin.rotation.z = -0.35;
  add(new T.BoxGeometry(1.4, 0.1, 1.8), body, -7.2, 0.95, 0);
  const tailRotor = new T.Group();
  tailRotor.position.set(-7.85, 1.7, 0.2);
  group.add(tailRotor);
  for (let i = 0; i < 2; i += 1)
    add(new T.BoxGeometry(0.16, 1.7, 0.04), dark, 0, 0, 0, tailRotor).rotation.z = (i * Math.PI) / 2;
  // the mast and the main rotor, four long blades
  add(new T.CylinderGeometry(0.15, 0.15, 0.7, 10), metal, 0.1, 2.35, 0);
  const rotor = new T.Group();
  rotor.position.set(0.1, 2.72, 0);
  group.add(rotor);
  add(new T.CylinderGeometry(0.32, 0.32, 0.22, 12), metal, 0, 0, 0, rotor);
  for (let i = 0; i < 4; i += 1) {
    const blade = add(new T.BoxGeometry(5.8, 0.06, 0.34), dark, 2.9, 0, 0, rotor);
    const arm = new T.Group();
    arm.rotation.y = (i * Math.PI) / 2;
    arm.add(blade);
    rotor.add(arm);
  }
  // the skids
  for (const side of [-1, 1]) {
    const skid = add(new T.CylinderGeometry(0.08, 0.08, 4.4, 8), metal, 0.6, -CHOPPER_GROUND + 0.08, side * 1.25);
    skid.rotation.z = Math.PI / 2;
    for (const dx of [-0.6, 1.8]) add(new T.CylinderGeometry(0.06, 0.06, 1.0, 6), metal, dx, -1.0, side * 1.15);
  }
  // the lights: red beacon on the tail, strobes
  const beacon = add(
    new T.SphereGeometry(0.12, 8, 6),
    keep(new T.MeshBasicMaterial({ color: '#ff2a2a' })),
    -7.9,
    2.55,
    0,
  );
  return {
    group,
    update: (seconds) => {
      rotor.rotation.y = seconds * 26;
      tailRotor.rotation.z = seconds * 40;
      beacon.visible = seconds % 1 < 0.5;
    },
    dispose: () => disposables.forEach((item) => item.dispose()),
  };
}

const smooth = (t: number) => {
  const k = Math.max(0, Math.min(1, t));
  return k * k * (3 - 2 * k);
};
// the chopper's way: off the helipad (at the gate's place on the apron), up, forward and low over the city, then
// a hover and straight down. Distances in metres along x; the destination is laid at `stopsAt`.
export function chopperProfile(seconds: number) {
  const total = Math.max(9, seconds);
  const lift = 2.5,
    settle = 3;
  const cruise = 70;
  const travelTime = total - lift - settle;
  // forward speed: speeding up after the lift, slowing before the hover (a trapezoid)
  const ramp = Math.min(2.5, travelTime / 3);
  const forwardAt = (t: number) => {
    const u = t - lift;
    if (u <= 0) return 0;
    if (u >= travelTime) return cruise * (travelTime - ramp);
    if (u < ramp) return (cruise * u * u) / (2 * ramp);
    if (u > travelTime - ramp) {
      const back = travelTime - u;
      return cruise * (travelTime - ramp) - (cruise * back * back) / (2 * ramp);
    }
    return cruise * (u - ramp / 2);
  };
  const distance = forwardAt(total);
  const heightAt = (t: number) => {
    if (t < lift) return 18 * smooth(t / lift);
    if (t < lift + ramp * 1.5) return 18 + (CHOPPER_HEIGHT - 18) * smooth((t - lift) / (ramp * 1.5));
    if (t < total - settle - ramp) return CHOPPER_HEIGHT;
    if (t < total - settle) return 18 + (CHOPPER_HEIGHT - 18) * (1 - smooth((t - (total - settle - ramp)) / ramp));
    return 18 * (1 - smooth((t - (total - settle)) / settle));
  };
  const start = GATE.x;
  const pointAt = (t: number): FlightPoint => {
    const x = start + forwardAt(t);
    const speed = forwardAt(t + 0.1) - forwardAt(t);
    const before = forwardAt(t) - forwardAt(Math.max(0, t - 0.1));
    // leaning into it as it speeds up, back as it slows
    const pitch = Math.max(-0.22, Math.min(0.18, -(speed - before) * 0.6));
    return {
      x,
      y: heightAt(t),
      z: GATE.z,
      pitch,
      heading: 0,
      gearDown: true,
      progress: Math.max(0, Math.min(1, t / total)),
    };
  };
  return { total, touchdown: total, landsAt: start + distance, stopsAt: start + distance, pointAt };
}
